import { useState, useEffect } from "react";
import {
  useUsesRecurlySubscriptionQuery,
  useGetRecurlyBillingDataQuery,
  useGetPremiumPlanPricesQuery,
  useCancelRecurlySubscriptionMutation,
  useSetDefaultRecurlyPaymentMethodMutation,
  useUpdateRecurlyPaymentMethodMutation,
  useDeleteRecurlyPaymentMethodMutation,
  CancelRecurlySubscriptionMutation,
} from "../queries/lol";
import { addCookieWithExpiration, removeCookie } from "@ugg/shared/utils/cookies";

const PREMIUM_COOKIE_NAME = "ugg_premium";
const PREMIUM_COOKIE_EXPIRATION_DAYS = 7;

function getPremiumCookieDomain(): string | undefined {
  if (typeof window !== "undefined" && window.location.hostname.includes("u.gg")) {
    return ".u.gg";
  }
  return undefined;
}

interface PlanPrice {
  amount: number;
  currency: string;
  planDuration: number;
  hasFreeTrial?: boolean;
  trialLength?: number;
  trialUnit?: string;
}

interface PricesByDuration {
  [key: number]: PlanPrice;
}

interface AddPaymentMethodResponse {
  success?: boolean | null;
  error?: string | null;
  paymentMethod?: {
    id?: string | null;
    type?: string | null;
    isDefault?: boolean | null;
    cardType?: string | null;
    lastFour?: string | null;
    paypalEmail?: string | null;
  } | null;
}

interface SetDefaultPaymentMethodResponse {
  success?: boolean | null;
  error?: string | null;
}

interface DeletePaymentMethodResponse {
  success?: boolean | null;
  error?: string | null;
}

type CancelSubscriptionResponse = NonNullable<CancelRecurlySubscriptionMutation["cancelRecurlySubscription"]>;

interface HookCallbacks<T> {
  onCompleted?: (data: T) => void;
  onError?: (error: string) => void;
}

export const usePremiumPlanPrices = () => {
  const { loading, data, error } = useGetPremiumPlanPricesQuery({
    context: { clientName: "lol" },
  });

  const planPrices: PlanPrice[] = (data?.getPlanPrices || []).filter((p): p is PlanPrice => p !== null && p !== undefined);

  const pricesByDuration: PricesByDuration = {};
  planPrices.forEach((p) => {
    if (p.planDuration) {
      pricesByDuration[p.planDuration] = p;
    }
  });

  return {
    data: { planPrices, pricesByDuration },
    loading,
    error,
  };
};

export const useBillingData = () => {
  const { loading, data, error, refetch } = useGetRecurlyBillingDataQuery({
    context: { clientName: "lol" },
  });

  type BillingData = NonNullable<NonNullable<typeof data>["getRecurlyBillingData"]>;
  type Invoice = NonNullable<BillingData["invoices"]>[number];
  type PaymentMethod = NonNullable<BillingData["paymentMethods"]>[number];

  const invoices = (data?.getRecurlyBillingData?.invoices || []).filter(Boolean) as NonNullable<Invoice>[];
  const paymentMethods = (data?.getRecurlyBillingData?.paymentMethods || []).filter(Boolean) as NonNullable<PaymentMethod>[];

  const billingData = {
    invoices,
    subscription: data?.getRecurlyBillingData?.subscription || null,
    paymentMethods,
    upcomingInvoice: data?.getRecurlyBillingData?.upcomingInvoice || null,
  };

  return {
    data: billingData,
    loading,
    error,
    refetch,
  };
};

export const useAddPaymentMethod = ({ onCompleted, onError }: HookCallbacks<AddPaymentMethodResponse> = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [addPaymentMethodMutation] = useUpdateRecurlyPaymentMethodMutation({
    context: { clientName: "lol" },
    onCompleted: (data) => {
      setIsLoading(false);
      if (data.updateRecurlyPaymentMethod?.success) {
        onCompleted?.(data.updateRecurlyPaymentMethod);
      } else {
        const errorMsg = data.updateRecurlyPaymentMethod?.error || "An error occurred";
        setError(errorMsg);
        onError?.(errorMsg);
      }
    },
    onError: (err) => {
      setIsLoading(false);
      const errorMsg = err.message || "An error occurred while adding your payment method";
      setError(errorMsg);
      onError?.(errorMsg);
    },
  });

  const addPaymentMethod = (recurlyToken: string) => {
    setIsLoading(true);
    setError(null);

    addPaymentMethodMutation({
      variables: {
        recurlyToken,
      },
    });
  };

  return { addPaymentMethod, isLoading, error };
};

export const useCancelSubscription = ({ onCompleted, onError }: HookCallbacks<CancelSubscriptionResponse> = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [cancelSubscriptionMutation] = useCancelRecurlySubscriptionMutation({
    context: { clientName: "lol" },
    onCompleted: (data) => {
      setIsLoading(false);
      if (data.cancelRecurlySubscription?.success) {
        onCompleted?.(data.cancelRecurlySubscription);
      } else {
        const errorMsg = data.cancelRecurlySubscription?.error || "An error occurred";
        setError(errorMsg);
        onError?.(errorMsg);
      }
    },
    onError: (err) => {
      setIsLoading(false);
      const errorMsg = err.message || "An error occurred while canceling your subscription";
      setError(errorMsg);
      onError?.(errorMsg);
    },
  });

  const cancelSubscription = () => {
    setIsLoading(true);
    setError(null);
    cancelSubscriptionMutation();
  };

  return { cancelSubscription, isLoading, error };
};

export const useSetDefaultPaymentMethod = ({ onCompleted, onError }: HookCallbacks<SetDefaultPaymentMethodResponse> = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [setDefaultPaymentMethodMutation] = useSetDefaultRecurlyPaymentMethodMutation({
    context: { clientName: "lol" },
    onCompleted: (data) => {
      setIsLoading(false);
      if (data.setDefaultRecurlyPaymentMethod?.success) {
        onCompleted?.(data.setDefaultRecurlyPaymentMethod);
      } else {
        const errorMsg = data.setDefaultRecurlyPaymentMethod?.error || "An error occurred";
        setError(errorMsg);
        onError?.(errorMsg);
      }
    },
    onError: (err) => {
      setIsLoading(false);
      const errorMsg = err.message || "An error occurred while setting default payment method";
      setError(errorMsg);
      onError?.(errorMsg);
    },
  });

  const setDefaultPaymentMethod = (recurlyToken: string) => {
    setIsLoading(true);
    setError(null);
    setDefaultPaymentMethodMutation({
      variables: {
        recurlyToken,
      },
    });
  };

  return { setDefaultPaymentMethod, isLoading, error };
};

export const useDeletePaymentMethod = ({ onCompleted, onError }: HookCallbacks<DeletePaymentMethodResponse> = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deletePaymentMethodMutation] = useDeleteRecurlyPaymentMethodMutation({
    context: { clientName: "lol" },
    onCompleted: (data) => {
      setIsLoading(false);
      if (data.deleteRecurlyPaymentMethod?.success) {
        onCompleted?.(data.deleteRecurlyPaymentMethod);
      } else {
        const errorMsg = data.deleteRecurlyPaymentMethod?.error || "An error occurred";
        setError(errorMsg);
        onError?.(errorMsg);
      }
    },
    onError: (err) => {
      setIsLoading(false);
      const errorMsg = err.message || "An error occurred while deleting payment method";
      setError(errorMsg);
      onError?.(errorMsg);
    },
  });

  const deletePaymentMethod = () => {
    setIsLoading(true);
    setError(null);
    deletePaymentMethodMutation();
  };

  return { deletePaymentMethod, isLoading, error };
};

export const useRecurlySubscriptionStatus = () => {
  const { data, loading, error } = useUsesRecurlySubscriptionQuery({
    context: { clientName: "lol" },
  });

  // success = true means active subscription
  const isActive = data?.usesRecurlySubscription?.success === true;

  // Set or remove premium cookie based on subscription status
  useEffect(() => {
    if (loading) return;

    const domain = getPremiumCookieDomain();
    if (isActive) {
      addCookieWithExpiration(PREMIUM_COOKIE_NAME, "1", PREMIUM_COOKIE_EXPIRATION_DAYS, domain);
    } else if (data?.usesRecurlySubscription) {
      // Only remove if we got a response (not just no data yet)
      removeCookie(PREMIUM_COOKIE_NAME, domain);
    }
  }, [isActive, loading, data?.usesRecurlySubscription]);

  return {
    data: data?.usesRecurlySubscription,
    isActive,
    loading,
    error,
  };
};
